import { useEffect } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { useTheme } from '@/hooks/use-theme'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { StorageKeys, StorageSchemas } from '@/lib/storage'
import { BROWSE_FOCUS_DEFAULTS, BROWSE_INACTIVE_DAY_OPTIONS } from '@/lib/browse-folder-focus'

interface UISettingsProps {
  form: UseFormReturn<any>
}

export function UISettings({ form }: UISettingsProps) {
  const { theme, setTheme } = useTheme()
  
  // Watch theme field and update immediately
  const currentTheme = form.watch('ui.theme')
  
  useEffect(() => {
    if (currentTheme && currentTheme !== theme) {
      setTheme(currentTheme)
    }
  }, [currentTheme, theme, setTheme])
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Appearance</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Customize the look and feel of Maater.
        </p>
        
        <FormField
          control={form.control}
          name="ui.theme"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Theme</FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormDescription>
                System uses the browser color preference (e.g. Chrome Settings - Appearance - Device, not a fixed Light/Dark).
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Layout</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Configure default layout preferences.
        </p>
        
        <FormField
          control={form.control}
          name="ui.defaultViewMode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Default View Mode</FormLabel>
              <FormControl>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="grid">Grid</SelectItem>
                    <SelectItem value="list">List</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormDescription>
                Default view mode for document lists
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="ui.sidebarCollapsed"
          render={({ field }) => (
            <FormItem className="flex items-center justify-between mt-4">
              <div className="space-y-0.5">
                <FormLabel>Sidebar Collapsed by Default</FormLabel>
                <FormDescription>
                  Start with sidebar collapsed on page load
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      <BrowseSidebarDefaultsSection />
    </div>
  )
}

function BrowseSidebarDefaultsSection() {
  const [focusBrowse, setFocusBrowse] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_ENABLED,
    defaultValue: BROWSE_FOCUS_DEFAULTS.focusBrowse,
    schema: StorageSchemas.boolean,
  })
  const [inactiveDays, setInactiveDays] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_INACTIVE_DAYS,
    defaultValue: BROWSE_FOCUS_DEFAULTS.inactiveDays,
    schema: StorageSchemas.number,
  })
  const [showActivityInTree, setShowActivityInTree] = usePersistedState({
    key: StorageKeys.BROWSE_SHOW_ACTIVITY_IN_TREE,
    defaultValue: BROWSE_FOCUS_DEFAULTS.showActivityInTree,
    schema: StorageSchemas.boolean,
  })
  const [focusMinChildren, setFocusMinChildren] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_MIN_CHILDREN,
    defaultValue: BROWSE_FOCUS_DEFAULTS.focusMinChildren,
    schema: StorageSchemas.number,
  })
  const [focusMaxVisible, setFocusMaxVisible] = usePersistedState({
    key: StorageKeys.BROWSE_FOCUS_MAX_VISIBLE,
    defaultValue: BROWSE_FOCUS_DEFAULTS.focusMaxVisible,
    schema: StorageSchemas.number,
  })

  return (
    <div id="browse-sidebar" className="scroll-mt-4">
      <h3 className="text-lg font-semibold mb-2">Browse sidebar</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Browse filter rules for the Documents tree. Focus browse on/off is one setting (here and on
        the Documents row context menu). Per-folder Show/Hide only applies when focus browse is on.
      </p>
      <div className="space-y-4 rounded-lg border border-border p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="settings-focus-browse">Focus browse</Label>
            <p className="text-xs text-muted-foreground">
              When on, hide inactive items and cap large folders; focus rails appear under qualifying
              parents. Same toggle as Documents row - Browse in the sidebar.
            </p>
          </div>
          <Switch
            id="settings-focus-browse"
            checked={focusBrowse}
            onCheckedChange={setFocusBrowse}
          />
        </div>
        <div className="space-y-2">
          <Label>Hide items inactive for</Label>
          <Select
            value={String(inactiveDays)}
            onValueChange={(v) => setInactiveDays(Number(v))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BROWSE_INACTIVE_DAY_OPTIONS.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {d} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-focus-min-children">Apply focus when parent has at least</Label>
          <p className="text-xs text-muted-foreground">
            Files or folders under one parent (e.g. filter implementations/ when it has many specs).
          </p>
          <Select
            value={String(focusMinChildren)}
            onValueChange={(v) => setFocusMinChildren(Math.max(1, Number(v)))}
          >
            <SelectTrigger id="settings-focus-min-children" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[5, 10, 15, 20, 30, 50].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} items
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="settings-focus-max-visible">Max visible items per large parent</Label>
          <p className="text-xs text-muted-foreground">
            Show the most recently active files and folders up to this limit (same cap for each).
          </p>
          <Select
            value={String(focusMaxVisible)}
            onValueChange={(v) => setFocusMaxVisible(Math.max(1, Number(v)))}
          >
            <SelectTrigger id="settings-focus-max-visible" className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 15, 20, 30, 50].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} items
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="settings-show-activity">Show activity in tree</Label>
            <p className="text-xs text-muted-foreground">
              Days since last view/edit next to folder names (files use the same setting; off by default).
            </p>
          </div>
          <Switch
            id="settings-show-activity"
            checked={showActivityInTree}
            onCheckedChange={setShowActivityInTree}
          />
        </div>
      </div>
    </div>
  )
}

